import { Euro, Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { CurrencyInput } from '@/components/ui/currency-input'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  FORM_CONTROL_BASE_CLASS,
  FORM_CONTROL_FOCUS_CLASS,
  FORM_CONTROL_FOCUS_WITHIN_CLASS,
  FORM_CONTROL_SELECT_OPEN_CLASS,
} from '@/components/ui/form-control-styles'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { EntityPicker } from '@/components/shared/EntityPicker'
import { ClientNameWithProspect } from '@/components/shared/StatusBadges'
import { DatePicker } from '@/components/shared'
import { Permit } from '@/components/guards/Permit'
import {
  FormAccordion,
  FormAccordionCard,
  FormBadge,
  FormFieldsCountBadge,
  FormInnerBlock,
} from '@/components/contracts/ContractFormAccordion'
import { clientsApi } from '@/services/clients'
import { formatIsoDateDisplay } from '@/lib/date'
import { formatCurrency, parseCurrencyInput } from '@/lib/format'
import { cn } from '@/lib/utils'
import {
  CONTRACT_TYPES,
  CONTRACT_TYPE_LABELS,
  type Client,
  type ContractType,
} from '@/types'
import { BILLING_FREQUENCIES, QUICK_DURATIONS, VAT_RATES } from './constants'
import {
  ContractTypeChip,
  FieldError,
  FormField,
  FrequencyCard,
  QuickDurationChip,
  RequiredLabel,
} from './FormControls'
import type { SetErrors, ShownErrors } from './types'

const figmaLabelClass =
  'flex h-4 w-full shrink-0 items-center gap-1 overflow-hidden whitespace-nowrap text-xs font-medium normal-case leading-4 text-[#171a20]'

const figmaFieldClass = cn(
  'h-10 rounded-xl border px-4 py-3 normal-case leading-[1.4] shadow-none placeholder:font-normal placeholder:text-[#6c7285]',
  FORM_CONTROL_BASE_CLASS,
  FORM_CONTROL_FOCUS_CLASS
)

const figmaPickerClass = cn(
  'h-10 rounded-xl border px-4 py-3 normal-case leading-[1.4] shadow-none hover:bg-[#eef0f5] [&>span]:font-normal [&>span>span]:font-normal',
  FORM_CONTROL_BASE_CLASS,
  FORM_CONTROL_SELECT_OPEN_CLASS
)

const figmaDateClass = cn(
  'h-10 rounded-xl border px-4 py-3 normal-case leading-[1.4] shadow-none [&_input]:text-sm [&_input]:font-normal [&_input]:normal-case [&_input]:leading-[1.4] [&_input]:text-[#171a20] [&_input]:placeholder:text-[#6c7285]',
  FORM_CONTROL_BASE_CLASS,
  FORM_CONTROL_FOCUS_WITHIN_CLASS
)

const figmaSelectClass = cn(
  'h-10 rounded-xl border px-4 py-3 normal-case leading-[1.4] shadow-none data-[placeholder]:font-normal [&>span]:font-normal [&>span>span]:font-normal',
  FORM_CONTROL_BASE_CLASS,
  FORM_CONTROL_SELECT_OPEN_CLASS
)

const figmaDisabledClass =
  'h-10 rounded-xl border-0 bg-[#e7e9ef] px-4 py-3 text-sm font-normal normal-case leading-[1.4] text-[#6c7285] disabled:cursor-default disabled:opacity-100'

const figmaAmountInclClass =
  'h-10 rounded-xl border border-[#ff9d75] bg-[#fff1ea] px-4 py-3 text-sm font-normal normal-case leading-[1.4] text-[#ff5a1f] disabled:cursor-default disabled:opacity-100'

interface Props {
  incompleteFields: number
  startDate: string
  endDate: string
  amountIncl: number
  shownErrors: ShownErrors
  setErrors: SetErrors
  clientId: string
  clientLabel: string
  selectedClientName?: string
  selectedClientCategory?: string
  setClientId: (id: string) => void
  setClientLabel: (label: string) => void
  title: string
  setTitle: (value: string) => void
  contractType: ContractType | ''
  setContractType: (value: ContractType | '') => void
  notes: string
  setNotes: (value: string) => void
  setStartDate: (value: string) => void
  setEndDate: (value: string) => void
  duration: string
  applyQuickDuration: (months: number) => void
  frequency: string
  setFrequency: (value: string) => void
  amountExcl: string
  setAmountExcl: (value: string) => void
  vatRate: string
  setVatRate: (value: string) => void
  byDueDate: string
}

export function ContractInformationSection({
  incompleteFields,
  startDate,
  endDate,
  amountIncl,
  shownErrors,
  setErrors,
  clientId,
  clientLabel,
  selectedClientName,
  selectedClientCategory,
  setClientId,
  setClientLabel,
  title,
  setTitle,
  contractType,
  setContractType,
  notes,
  setNotes,
  setStartDate,
  setEndDate,
  duration,
  applyQuickDuration,
  frequency,
  setFrequency,
  amountExcl,
  setAmountExcl,
  vatRate,
  setVatRate,
  byDueDate,
}: Props) {
  const { t } = useTranslation('contracts')
  const navigate = useNavigate()

  return (
    <FormAccordionCard
      value='contract-information'
      step={1}
      title={t('form.sections.contractInformation')}
      complete={incompleteFields === 0}
      stepClassName='font-medium'
      titleClassName='font-semibold normal-case'
      badge={
        <span className='flex shrink-0 items-center gap-3'>
          {incompleteFields === 0 && startDate && endDate && (
            <FormBadge className='font-medium'>
              {formatIsoDateDisplay(startDate)} -{' '}
              {formatIsoDateDisplay(endDate)}
            </FormBadge>
          )}
          {incompleteFields === 0 && (
            <FormBadge tone='success' className='font-medium'>
              {t('form.amountInclBadge', {
                amount: formatCurrency(amountIncl, 'EUR', 2),
              })}
            </FormBadge>
          )}
          <FormFieldsCountBadge
            count={incompleteFields}
            label={t('form.fieldsToFill', {
              count: incompleteFields,
            })}
            completedLabel={t('form.fieldsFilled')}
            className='font-medium'
          />
        </span>
      }
    >
      <FormAccordion
        defaultValue={['customer', 'periods', 'finance']}
        className='flex flex-col gap-4'
      >
        <FormInnerBlock
          value='customer'
          title={t('form.sections.customerInformation')}
          titleClassName='text-sm'
        >
          <div
            className={cn(
              'flex flex-col gap-4 sm:flex-row',
              shownErrors.client_id ? 'sm:items-center' : 'sm:items-end'
            )}
          >
            <FormField className='min-w-0 flex-1'>
              <RequiredLabel compact className='font-medium'>
                {t('form.client.label')}
              </RequiredLabel>
              <EntityPicker<Client>
                queryKey='clients'
                preload
                variant='search'
                placeholder={t('form.client.searchPlaceholder')}
                fetcher={({ search, limit, page }) =>
                  clientsApi.list({ search, limit, page })
                }
                getOptionLabel={c => c.name}
                getOptionValue={c => c.id}
                renderOption={c => (
                  <ClientNameWithProspect name={c.name} category={c.category} />
                )}
                renderValue={(label, item) => (
                  <ClientNameWithProspect
                    name={label}
                    category={item?.category ?? selectedClientCategory}
                  />
                )}
                value={clientId || null}
                initialLabel={clientLabel || selectedClientName}
                onChange={(v, client) => {
                  setClientId(v)
                  setClientLabel(client.name)
                  setErrors(e => ({ ...e, client_id: '' }))
                }}
                className={cn(
                  figmaPickerClass,
                  shownErrors.client_id && 'border border-destructive'
                )}
              />
              <FieldError message={shownErrors.client_id} />
            </FormField>
            <Permit module='clients' action='edit'>
              <Button
                type='button'
                variant='outline'
                className='h-10 shrink-0 gap-2 rounded-xl border border-dashed border-[#ff5a1f] bg-white p-3 text-sm font-medium normal-case leading-none text-[#ff5a1f] hover:bg-[#fff1ea]'
                onClick={() => navigate('/clients/new')}
              >
                <Plus className='size-4' />
                {t('form.newCustomer')}
              </Button>
            </Permit>
          </div>

          <FormField>
            <Label htmlFor='title' className={figmaLabelClass}>
              {t('form.contractInfo.titleLabel')}
            </Label>
            <Input
              id='title'
              value={title}
              maxLength={150}
              placeholder={t('form.contractInfo.titlePlaceholder')}
              onChange={e => {
                setTitle(e.target.value)
                if (e.target.value.trim()) {
                  setErrors(err => ({ ...err, title: '' }))
                }
              }}
              className={cn(
                figmaFieldClass,
                shownErrors.title && 'border border-destructive'
              )}
            />
            <FieldError message={shownErrors.title} />
          </FormField>

          <FormField gap='md'>
            <RequiredLabel compact className='font-medium'>
              {t('form.contractInfo.typeLabel')}
            </RequiredLabel>
            <div className='flex flex-wrap gap-2'>
              {CONTRACT_TYPES.map(ct => (
                <ContractTypeChip
                  key={ct}
                  type={ct}
                  selected={contractType === ct}
                  onSelect={() => {
                    setContractType(ct)
                    setErrors(e => ({ ...e, contract_type: '' }))
                  }}
                  className={cn(
                    'px-3 py-2.5 text-xs font-normal normal-case leading-4'
                  )}
                >
                  {CONTRACT_TYPE_LABELS[ct]}
                </ContractTypeChip>
              ))}
            </div>
            <FieldError message={shownErrors.contract_type} />
          </FormField>

          <FormField>
            <Label htmlFor='notes' className={figmaLabelClass}>
              {t('form.contractInfo.notesLabel')}
            </Label>
            <Textarea
              id='notes'
              value={notes}
              maxLength={5000}
              rows={3}
              placeholder={t('form.contractInfo.notesPlaceholder')}
              onChange={e => setNotes(e.target.value)}
              className={cn('min-h-20 resize-y', figmaFieldClass)}
            />
          </FormField>
        </FormInnerBlock>

        <FormInnerBlock
          value='periods'
          title={t('form.sections.periodsDates')}
          titleClassName='text-sm'
        >
          <div className='flex flex-col gap-4 border-b border-[#e7e9ef] pb-4'>
            <div className='grid items-start gap-4 sm:grid-cols-3'>
              <FormField>
                <RequiredLabel htmlFor='start' compact className='font-medium'>
                  {t('form.periods.startDate')}
                </RequiredLabel>
                <DatePicker
                  id='start'
                  value={startDate}
                  onChange={value => {
                    setStartDate(value)
                    if (value) setErrors(e => ({ ...e, start_date: '' }))
                  }}
                  placeholder={t('form.periods.datePlaceholder')}
                  className={cn(
                    figmaDateClass,
                    shownErrors.start_date && 'border border-destructive'
                  )}
                  error={!!shownErrors.start_date}
                />
                <FieldError message={shownErrors.start_date} />
              </FormField>
              <FormField>
                <RequiredLabel htmlFor='end' compact className='font-medium'>
                  {t('form.periods.endDate')}
                </RequiredLabel>
                <DatePicker
                  id='end'
                  value={endDate}
                  onChange={value => {
                    setEndDate(value)
                    if (value) setErrors(e => ({ ...e, end_date: '' }))
                  }}
                  placeholder={t('form.periods.datePlaceholder')}
                  className={cn(
                    figmaDateClass,
                    shownErrors.end_date && 'border border-destructive'
                  )}
                  error={!!shownErrors.end_date}
                  min={startDate || undefined}
                />
                <FieldError message={shownErrors.end_date} />
              </FormField>
              <FormField>
                <Label className={figmaLabelClass}>
                  {t('form.periods.duration')}
                </Label>
                <Input
                  value={duration}
                  disabled
                  readOnly
                  className={figmaDisabledClass}
                />
              </FormField>
            </div>
            <div className='flex flex-wrap items-center gap-2'>
              <span className='text-xs font-normal normal-case leading-4 text-[#171a20]'>
                {t('form.periods.quickDuration')}
              </span>
              {QUICK_DURATIONS.map(q => (
                <QuickDurationChip
                  key={q.id}
                  onSelect={() => applyQuickDuration(q.months)}
                  className={cn(
                    'text-xs font-normal normal-case leading-4'
                  )}
                >
                  {t(`form.periods.quick.${q.id}`)}
                </QuickDurationChip>
              ))}
            </div>
          </div>

          <FormField gap='md'>
            <RequiredLabel compact className='font-medium'>
              {t('form.periods.billingFrequency')}
            </RequiredLabel>
            <div className='grid w-full grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4'>
              {BILLING_FREQUENCIES.map(opt => (
                <FrequencyCard
                  key={opt.value}
                  selected={frequency === opt.value}
                  onSelect={() => {
                    setFrequency(opt.value)
                    setErrors(e => ({ ...e, frequency: '' }))
                  }}
                  title={t(`form.periods.frequencyCard.${opt.value}.label`)}
                  hint={t(
                    `form.periods.frequencyCard.${opt.value}.description`
                  )}
                  titleClassName='text-sm font-medium normal-case'
                  hintClassName='text-xs normal-case leading-4'
                />
              ))}
            </div>
            <FieldError message={shownErrors.frequency} />
          </FormField>
        </FormInnerBlock>

        <FormInnerBlock
          value='finance'
          title={t('form.sections.finance')}
          titleClassName='text-sm'
        >
          <div className='grid items-start gap-4 sm:grid-cols-2 lg:grid-cols-4'>
            <FormField>
              <RequiredLabel htmlFor='excl' compact className='font-medium'>
                {t('form.finance.amountExcl')}
              </RequiredLabel>
              <div className='relative w-full'>
                <Euro className='pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#6c7285]' />
                <CurrencyInput
                  id='excl'
                  value={amountExcl}
                  onValueChange={raw => {
                    setAmountExcl(raw)
                    const parsed = parseCurrencyInput(raw)
                    if (parsed !== null && parsed > 0) {
                      setErrors(err => ({ ...err, amount: '' }))
                    }
                  }}
                  className={cn(
                    figmaFieldClass,
                    'pl-10',
                    shownErrors.amount && 'border border-destructive'
                  )}
                />
              </div>
              <FieldError message={shownErrors.amount} />
            </FormField>
            <FormField>
              <RequiredLabel compact className='font-medium'>
                {t('form.finance.vatRate')}
              </RequiredLabel>
              <Select value={vatRate} onValueChange={setVatRate}>
                <SelectTrigger className={figmaSelectClass}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {VAT_RATES.map(r => (
                    <SelectItem
                      key={r}
                      value={String(r)}
                      className='text-sm font-normal normal-case'
                    >
                      {r}%
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
            <FormField>
              <Label className={figmaLabelClass}>
                {t('form.finance.amountIncl')}
              </Label>
              <Input
                value={formatCurrency(amountIncl, 'EUR', 2)}
                disabled
                readOnly
                className={figmaAmountInclClass}
              />
            </FormField>
            <FormField>
              <Label className={figmaLabelClass}>
                {t('form.finance.byDueDate')}
              </Label>
              <Input
                value={byDueDate}
                disabled
                readOnly
                className={figmaDisabledClass}
              />
            </FormField>
          </div>
        </FormInnerBlock>
      </FormAccordion>
    </FormAccordionCard>
  )
}
